import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "helvar-spec-wizard-v5";
const EPBD_BASELINE = ["occupancy", "daylight", "scheduling", "emergency"];
const EPBD_DIGITAL_BASELINE = ["energy", "bms"];

const defaultInputs = {
  project_name: "",
  client_name: "",
  country: "",
  project_type: "",
  segment: "",
  sub_segment: "",
  applications: [],
  architecture: "",
  functional_requirements: [...EPBD_BASELINE],
  digital_services: [...EPBD_DIGITAL_BASELINE],
};

const WizardContext = createContext(null);

function loadPersistedState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...defaultInputs, _epbd_seeded: true };
    const parsed = JSON.parse(raw);
    return { ...defaultInputs, ...parsed, _epbd_seeded: true };
  } catch {
    return { ...defaultInputs, _epbd_seeded: true };
  }
}

export function WizardProvider({ children }) {
  const [inputs, setInputs] = useState(loadPersistedState);
  const [result, setResult] = useState(null);
  const [lead, setLead] = useState(null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(inputs));
    } catch {
      /* ignore quota */
    }
  }, [inputs]);

  const setField = (key, value) =>
    setInputs((prev) => ({ ...prev, [key]: value }));

  const setFields = (patch) =>
    setInputs((prev) => ({ ...prev, ...patch }));

  const toggleArrayItem = (key, value) =>
    setInputs((prev) => {
      const arr = prev[key] || [];
      return {
        ...prev,
        [key]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value],
      };
    });

  const reset = () => {
    const seeded = { ...defaultInputs, _epbd_seeded: true };
    setInputs(seeded);
    setResult(null);
    setLead(null);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(seeded));
    } catch {
      /* ignore */
    }
  };

  const value = useMemo(
    () => ({ inputs, setField, setFields, toggleArrayItem, reset, result, setResult, lead, setLead }),
    [inputs, result, lead]
  );

  return <WizardContext.Provider value={value}>{children}</WizardContext.Provider>;
}

export function useWizard() {
  const ctx = useContext(WizardContext);
  if (!ctx) throw new Error("useWizard must be used within WizardProvider");
  return ctx;
}
